import { Flex, HStack, Icon, Image, Link, Stack, Text } from "@chakra-ui/react"
import { Button, Heading } from "DesignSystem/components"
import { LinkExternalIcon } from "Icons/LinkExternalIcon"
import {
  DisplayModal,
  DisplayModalProps,
} from "Shared/components/DisplayModal/DisplayModal"
import { useDismissibleNotice } from "Shared/hooks/useDismissibleNotice"
import React from "react"
import { useTrackEvent } from "~/api/generated/usabilityhub-components"
import SupportingImage from "./linkedin-promo.png"

type LinkedInOptOutModalProps = Pick<
  DisplayModalProps,
  "isOpen" | "onClose"
> & {
  onGetVerified: () => void
  source: string
}

export const LinkedInOptOutModal: React.FC<LinkedInOptOutModalProps> = ({
  isOpen,
  onClose,
  onGetVerified,
  source,
}) => {
  const { dismiss } = useDismissibleNotice("linkedin_verification_card")
  const { mutate: trackEvent } = useTrackEvent({})

  const handleGetVerified = () => {
    onGetVerified()
  }

  const handleDismiss = () => {
    dismiss()
    trackEvent({
      body: {
        event: "panelist_linkedin_verification_dismissed",
        event_properties: {
          source,
        },
      },
    })
    onClose()
  }

  return (
    <DisplayModal isOpen={isOpen} onClose={onClose}>
      <DisplayModal.TwoPaneContent
        topHeight={["300px", "300px", "auto"]}
        content={
          <Flex
            h="full"
            w="full"
            bgColor="darkNeutral.opaque.800"
            borderTopRadius="24px"
            borderBottomRadius={[0, "24px"]}
            justifyContent="center"
            alignItems="center"
          >
            <Image
              src={SupportingImage}
              w="full"
              maxW={["340px", "340px", "380px"]}
              objectFit="contain"
              alt="Researchers prefer LinkedIn verified testers"
            />
          </Flex>
        }
        controls={
          <HStack justify="end" align="center" gap={2}>
            <Button variant="secondary" onClick={handleGetVerified}>
              Get LinkedIn verified
            </Button>
            <Button variant="primary" onClick={handleDismiss}>
              Skip for now
            </Button>
          </HStack>
        }
      >
        <Stack
          gap={4}
          minH={[0, "396px"]}
          justifyContent={["flex-start", "flex-start", "center"]}
        >
          <Heading as="h2" textStyle="ds.heading.emphasized">
            Are you sure you want to skip LinkedIn verification?
          </Heading>
          <Stack gap={3} textStyle="ds.paragraph.emphasized">
            <Text>
              Connecting and verifying your LinkedIn profile helps match you
              with studies that align with your profession, including those that
              may offer higher pay. Skipping this step may limit your
              opportunities.
            </Text>
            <Text>
              Don{"\u2019"}t have LinkedIn?{" "}
              <Link href="https://www.linkedin.com/signup/" isExternal>
                Create a free account <Icon as={LinkExternalIcon} />
              </Link>
            </Text>
          </Stack>
          <Text textStyle="ds.paragraph.primary" color="ds.text.subtle">
            If you don't have a professional background, you can continue
            without linking an account.
          </Text>
        </Stack>
      </DisplayModal.TwoPaneContent>
    </DisplayModal>
  )
}
